import { HttpClient, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { environment } from 'src/environments/environment';
import { ReportAdditifRequest, ReportAdditifResponseDto, ReportTaskDetailsDTO, DemandeReportsResponse, ReportValidationRequest } from '@/types';
import { IResponsePaginateRaw } from '@/types/generic-responses.types';

@Injectable({
  providedIn: 'root',
})
export class ReportsService {
  BASEURL = `${environment.api.reportUrl}/report-additifs`;
  FLOWABLEURL = `${environment.api.reportUrl}/flowable/report`;
  DOCURL = `${environment.api.reportUrl}/report/documents`;
  OLDURL = `${environment.api.reportUrl}/demande-reports`;
  httpClient = inject(HttpClient);

  getReportsByAc(): Observable<DemandeReportsResponse> {
    return this.httpClient.get<DemandeReportsResponse>(`${this.OLDURL}/redacteur`);
  }

  getDemandeReportsByDac(dacId: number): Observable<any[]> {
    return this.httpClient.get<any[]>(`${this.OLDURL}/by-dac/${dacId}`);
  }

  getPaginatedReports(params: any): Observable<IResponsePaginateRaw<ReportAdditifResponseDto>> {
    let httpParams = new HttpParams();
    if (params?.page !== undefined) {
      httpParams = httpParams.set('page', params.page.toString());
    }
    if (params?.size !== undefined) {
      httpParams = httpParams.set('size', params.size.toString());
    }
    if (params?.sort) {
      httpParams = httpParams.set('sort', params.sort);
    }
    if (params?.search) {
      httpParams = httpParams.set('search', params.search);
    }
    return this.httpClient.get<IResponsePaginateRaw<ReportAdditifResponseDto>>(`${this.BASEURL}`, {
      params: httpParams,
    });
  }

  createReport(request: ReportAdditifRequest): Observable<any> {
    return this.httpClient.post<any>(`${this.BASEURL}`, request);
  }

  getReportById(id: number): Observable<ReportAdditifResponseDto> {
    return this.httpClient.get<ReportAdditifResponseDto>(`${this.BASEURL}/${id}`);
  }

  updateReport(id: number, request: Partial<ReportAdditifRequest>): Observable<any> {
    return this.httpClient.put<any>(`${this.BASEURL}/${id}`, request);
  }

  startReportProcess(reportId: number): Observable<any> {
    return this.httpClient.post<any>(`${this.FLOWABLEURL}/${reportId}/start`, {});
  }

  validateTask(taskId: string, request: ReportValidationRequest): Observable<ReportTaskDetailsDTO> {
    return this.httpClient.post<ReportTaskDetailsDTO>(`${this.FLOWABLEURL}/tasks/${taskId}/validate`, request);
  }

  validateTaskWithModel(taskId: string, request: ReportValidationRequest, file?: File): Observable<ReportTaskDetailsDTO> {
    const formData = new FormData();
    // Assuming backend parses @ModelAttribute properly by fields or a single JSON string if configured so.
    // The user's backend uses @ModelAttribute ReportValidationRequest requestJson.
    // Usually Spring expects form fields or a JSON blob. We will send fields.
    if (request.validate !== undefined) formData.append('validate', String(request.validate));
    if (request.comment) formData.append('comment', request.comment);
    if (request.comId) formData.append('comId', String(request.comId));
    if (request.cncmpDecision !== undefined) formData.append('cncmpDecision', String(request.cncmpDecision));
    if (request.assigneeUserId) formData.append('assigneeUserId', request.assigneeUserId);
    if (request.avis) formData.append('avis', request.avis);
    if (request.docIdNote) formData.append('docIdNote', String(request.docIdNote));
    if (request.dateReport) formData.append('dateReport', String(request.dateReport));

    if (file) {
      formData.append('modelReportFile', file);
    }

    return this.httpClient.post<ReportTaskDetailsDTO>(`${this.FLOWABLEURL}/tasks/${taskId}/validate_with_model`, formData);
  }

  validateAvisNoteRapporteur(taskId: string, request: ReportValidationRequest, file?: File): Observable<ReportTaskDetailsDTO> {
    const formData = new FormData();
    if (request.validate !== undefined) formData.append('validate', String(request.validate));
    if (request.comment) formData.append('comment', request.comment);
    if (request.avis) formData.append('avis', request.avis);
    if (request.dateReport) formData.append('dateReport', String(request.dateReport));
    if (file) {
      formData.append('noteFile', file);
    }

    return this.httpClient.post<ReportTaskDetailsDTO>(`${this.FLOWABLEURL}/tasks/${taskId}/validate_avis_note_rapporteur`, formData);
  }

  getTaskDetailsByReportId(reportId: number): Observable<ReportTaskDetailsDTO> {
    return this.httpClient.get<ReportTaskDetailsDTO>(`${this.FLOWABLEURL}/tasks/by-report/${reportId}`);
  }

  generateDocx(reportId: number): Observable<Blob> {
    return this.httpClient.get(`${this.DOCURL}/${reportId}/docx`, { responseType: 'blob' });
  }

  downloadTemplatePdf(reportId: number): Observable<Blob> {
    return this.httpClient.get(`${this.FLOWABLEURL}/${reportId}/template/pdf`, { responseType: 'blob' });
  }

  downloadModelReport(alfrescoId: string): Observable<any> {
    return this.httpClient.post<any>(`${environment.api.paramsUrl}/docs/download/model-report`, { id: alfrescoId });
  }

  downloadAvisReport(alfrescoId: string): Observable<any> {
    return this.httpClient.post<any>(`${environment.api.paramsUrl}/docs/download/avis-report`, { id: alfrescoId });
  }

  uploadSignedDoc(reportId: number, templateFile: File): Observable<void> {
  const formData = new FormData();
  formData.append('file', templateFile);
  return this.httpClient.post<void>(`${this.BASEURL}/${reportId}/upload-signed-document`, formData);
  }
}

